import { format } from 'd3-format';
import { path, map, pathOr } from 'ramda';

import { Type as TopMetroView } from 'components/TopMetros/TopMetros.helpers';
import { Timeslice } from 'scenes/Report/Report.helper';

type KeyMetricView = TopMetroView | 'Lift';

type BaseLiftResults = {
  periodStart: Date | string;
  exposedAov: number;
  exposedTotalSales: number;
  count: number;
};

type LiftResults = BaseLiftResults & {
  periodEnd: Date | string;
  controlAov: number;
  totalSales: number;
  scaledControlSales: number;
  scaledControlTransactionCount: number;
};

type Scale = {
  periodStart: string | number;
  y: number;
};

export type DataType = 'control' | 'transactions' | 'none';

type TransactionSummary = {
  transactionDate: Date;
  numberOfTransactions: number;
  avgAov: number;
  totalTransactionAmount: number;
};

type SalesAverage = {
  control: number;
  exposed: number;
};

type DataResults = LiftResults | BaseLiftResults;

type Data = {
  type: DataType;
  results: DataResults[];
  exposedAverage?: number;
  timeslice?: Timeslice;
};

type ControlGroup = {
  liftResults: LiftResults[];
  salesAverage: SalesAverage;
};

type QueryData = {
  data: {
    controlGroup: ControlGroup;
    transactionSummary: TransactionSummary[];
  };
};

const chartMargin = {
  top: 20,
  bottom: 10,
};

const getTransactionsResults = path<TransactionSummary[]>([
  'data',
  'transactionSummary',
  'result',
]);

const getLiftResults = path<LiftResults[]>([
  'data',
  'controlGroup',
  'liftResults',
]);

const getExposedAverage = pathOr<number>(0, [
  'data',
  'controlGroup',
  'salesAverages',
  'exposed',
]);

const getTimeslice = path<Timeslice>([
  'data',
  'transactionSummary',
  'timeslice',
]);

const mapTransactions = map<TransactionSummary, BaseLiftResults>(
  ({
    avgAov,
    numberOfTransactions,
    totalTransactionAmount,
    transactionDate,
  }) => ({
    periodStart: transactionDate,
    exposedAov: avgAov,
    exposedTotalSales: totalTransactionAmount,
    count: numberOfTransactions,
  }),
);

const getResults = (data: QueryData, keyMetricView: KeyMetricView): Data => {
  const liftResults = getLiftResults(data);
  const transactionsResults = getTransactionsResults(data);

  if (liftResults) {
    return {
      results: liftResults,
      exposedAverage: getExposedAverage(data),
      type: 'control',
    };
  }

  if (transactionsResults) {
    return {
      results: mapTransactions(transactionsResults),
      timeslice: getTimeslice(data),
      type: 'transactions',
    };
  }

  return { results: [], type: 'none' };
};

const tickFormatter = (formatType = '$.0f') => (number: number) =>
  format(formatType)(number);

export {
  LiftResults,
  Data,
  Scale,
  tickFormatter,
  chartMargin,
  getLiftResults,
  getResults,
};
